import type { Command } from '@tiptap/core';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const pluginName = 'BlurHighlight';
const blur = 'blur';
const focus = 'focus';

declare module '@tiptap/core' {
  interface Commands {
    [pluginName]: {
      removeBlurHighlight: () => Command;
    };
  }
}

export const BlurHighlight = Extension.create({
  name: pluginName,

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey(pluginName),

        state: {
          init(_config) {
            return DecorationSet.empty;
          },
          apply: (transaction, oldState) => {
            const { selection, doc } = transaction;
            const decoTransform = transaction.getMeta(pluginName);
            const hasSelection = selection && selection.from !== selection.to;

            if (!hasSelection || decoTransform?.action === focus) {
              return DecorationSet.empty;
            }

            if (hasSelection && decoTransform?.action === blur) {
              const decoration = Decoration.inline(selection.from, selection.to, {
                class: 'blur-highlight',
              });

              return DecorationSet.create(doc, [decoration]);
            }

            return oldState;
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
          handleDOMEvents: {
            blur: view => {
              const { tr } = view.state;

              const transaction = tr.setMeta(pluginName, {
                from: tr.selection.from,
                to: tr.selection.to,
                action: blur,
              });

              view.dispatch(transaction);
            },
            focus: view => {
              const { state, dispatch } = view;
              const transaction = state.tr.setMeta(pluginName, {
                action: focus,
              });
              dispatch(transaction);
              return false;
            },
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      removeBlurHighlight:
        () =>
        ({ state, dispatch }) => {
          const transaction = state.tr
            .setMeta(pluginName, {
              action: focus,
            })
            .setSelection(TextSelection.near(state.doc.resolve(0)));

          dispatch(transaction);
          return true;
        },
    };
  },
});
